/**
 * Rolling last-7-local-calendar-day activity for member management (oldest → newest, today last).
 */

/**
 * Debug logs for empty last-7 circles. Enable any of:
 * - `npm start` (NODE_ENV=development)
 * - `localStorage.setItem('debugMemberLast7', '1')` then refresh
 * - `.env`: `REACT_APP_DEBUG_MEMBER_LAST7=true` then rebuild
 */
export function isMemberLast7DebugLogEnabled(): boolean {
  if (typeof process !== 'undefined' && process.env.REACT_APP_DEBUG_MEMBER_LAST7 === 'true') {
    return true;
  }
  if (typeof window !== 'undefined') {
    try {
      if (window.localStorage?.getItem('debugMemberLast7') === '1') return true;
    } catch {
      /* ignore */
    }
  }
  return typeof process !== 'undefined' && process.env.NODE_ENV === 'development';
}

export function logMemberLast7(message: string, payload?: unknown): void {
  if (!isMemberLast7DebugLogEnabled()) return;
  if (payload !== undefined) {
    console.info(`[MemberLast7Days] ${message}`, payload);
  } else {
    console.info(`[MemberLast7Days] ${message}`);
  }
}

export type MemberLast7DaysCompletion = {
  completed: number;
  total: number;
  /** Length 7: index 0 = six days ago, index 6 = today (local calendar). */
  daysWithCompletion: boolean[];
};

const YMD_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** Local calendar YYYY-MM-DD (user’s timezone). */
export function localCalendarDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Parse YYYY-MM-DD as a local calendar date (avoids UTC-only Date parsing pitfalls). */
function parseYmdAsLocal(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function addCalendarDaysToYmd(ymd: string, deltaDays: number): string {
  const d = parseYmdAsLocal(ymd);
  d.setDate(d.getDate() + deltaDays);
  return localCalendarDateKey(d);
}

/** Widen [first, last] by one day each side so DB sessions aren’t dropped on timezone boundaries. */
export function paddedQueryRangeFromDateKeys(dateKeys: string[]): { fromStr: string; toStr: string } {
  if (dateKeys.length < 7) {
    const k = getRollingLast7LocalDateKeys();
    return {
      fromStr: addCalendarDaysToYmd(k[0], -1),
      toStr: addCalendarDaysToYmd(k[6], 1),
    };
  }
  return {
    fromStr: addCalendarDaysToYmd(dateKeys[0], -1),
    toStr: addCalendarDaysToYmd(dateKeys[6], 1),
  };
}

/** Wider window for member cards (last / next workout) while reusing the same session fetch as last-7 dots. */
export function paddedWideRangeForMemberCardSessions(): { fromStr: string; toStr: string } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayKey = localCalendarDateKey(today);
  const fromKey = addCalendarDaysToYmd(todayKey, -400);
  const toKey = addCalendarDaysToYmd(todayKey, 120);
  return {
    fromStr: addCalendarDaysToYmd(fromKey, -1),
    toStr: addCalendarDaysToYmd(toKey, 1),
  };
}

export function getRollingLast7LocalDateKeys(): string[] {
  const keys: string[] = [];
  for (let offset = 6; offset >= 0; offset--) {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - offset);
    keys.push(localCalendarDateKey(d));
  }
  return keys;
}

/**
 * Calendar day for a scheduled_date value from the API.
 * - Plain `YYYY-MM-DD` (Postgres `date`) is used as-is (canonical workout day).
 * - Full ISO datetimes use the viewer’s **local** calendar day (slice(0,10) is UTC and often wrong).
 */
export function calendarDayKeyFromScheduledDate(scheduledDate: string): string {
  if (!scheduledDate?.trim()) return '';
  const s = scheduledDate.trim();
  if (YMD_ONLY.test(s)) {
    return s;
  }
  const t = Date.parse(s);
  if (Number.isNaN(t)) {
    return s.length >= 10 ? s.slice(0, 10) : s;
  }
  return localCalendarDateKey(new Date(t));
}

/** @deprecated Use calendarDayKeyFromScheduledDate */
export function scheduledDateToYmdKey(scheduledDate: string): string {
  return calendarDayKeyFromScheduledDate(scheduledDate);
}

/** Which circle to fill: always the **scheduled** workout day (matches calendar / member detail). */
function sessionDotDayKey(session: SessionLike): string {
  return calendarDayKeyFromScheduledDate(session.scheduledDate);
}

function normUuid(id: string): string {
  return id.replace(/-/g, '').toLowerCase().trim();
}

export function trainerIdsMatch(a: string | undefined | null, b: string | undefined | null): boolean {
  if (a == null || b == null) return false;
  const ta = String(a).trim();
  const tb = String(b).trim();
  if (ta === tb) return true;
  return normUuid(ta) === normUuid(tb);
}

/**
 * Trainer roster / Member Detail scope: only sessions this trainer placed on the calendar.
 * Null / member / other-trainer `assignedBy` → out of scope (PRD trainer-scoped-member-sessions).
 */
export function sessionIsFromTrainer(s: SessionLike, trainerId: string): boolean {
  return trainerIdsMatch(s.assignedBy, trainerId);
}

/** Plan assignment scope: `users_workoutplan.assigned_by` must match viewing trainer. */
export function planAssignmentIsFromTrainer(
  assignment: { assignedBy?: string | null },
  trainerId: string
): boolean {
  return trainerIdsMatch(assignment.assignedBy, trainerId);
}

export type SessionLike = {
  scheduledDate: string;
  status: string;
  completedAt?: string;
  workoutId?: string;
  /** Who placed this session on the member calendar (`workout_session.assigned_by`). */
  assignedBy?: string | null;
  workout?: { id?: string; createdBy?: string };
  /** @deprecated Prefer assignedBy for trainer scoping; retained for embeds/debug. */
  planCreatedBy?: string;
  usersWorkoutplanId?: string | null;
};

/** True when the session should count as a finished workout for the last-7 dots. */
export function sessionCountsAsCompletedWorkout(s: SessionLike): boolean {
  if (String(s.status).toLowerCase() === 'completed') return true;
  const ca = s.completedAt;
  return ca != null && String(ca).trim() !== '';
}

function calendarDaysBetweenEarlierToLater(earlierYmd: string, laterYmd: string): number {
  const a = parseYmdAsLocal(earlierYmd);
  const b = parseYmdAsLocal(laterYmd);
  return Math.round((b.getTime() - a.getTime()) / 86400000);
}

function formatLastWorkoutRelative(lastWorkoutDayYmd: string, todayYmd: string): string {
  const diff = calendarDaysBetweenEarlierToLater(lastWorkoutDayYmd, todayYmd);
  if (diff <= 0) return 'today';
  if (diff === 1) return 'yesterday';
  return `${diff} days ago`;
}

function formatNextWorkoutRelative(nextDayYmd: string, todayYmd: string): string {
  const diff = calendarDaysBetweenEarlierToLater(todayYmd, nextDayYmd);
  if (diff <= 0) return 'today';
  if (diff === 1) return 'tomorrow';
  if (diff < 7) return `in ${diff} days`;

  // Use coarse relative units (matches product tone of “X days ago”)
  const weeks = Math.round(diff / 7);
  if (diff < 30) {
    return weeks === 1 ? 'in 1 week' : `in ${weeks} weeks`;
  }

  const months = Math.round(diff / 30);
  return months === 1 ? 'in 1 month' : `in ${months} months`;
}

function sessionCompletionSortTime(s: SessionLike): number {
  if (s.completedAt != null && String(s.completedAt).trim() !== '') {
    const t = Date.parse(s.completedAt);
    if (!Number.isNaN(t)) return t;
  }
  const dk = calendarDayKeyFromScheduledDate(s.scheduledDate);
  return parseYmdAsLocal(dk).getTime();
}

export type MemberCardWorkoutSummary = {
  lastWorkoutLine: string;
  nextWorkoutLine: string;
};

/**
 * Copy for member management cards: last completed and next scheduled/in-progress workout (trainer-scoped, same rules as last-7 dots).
 */
export function computeMemberCardWorkoutSummaryFromSessions(
  sessions: SessionLike[],
  trainerId: string,
  options?: { todayYmd?: string }
): MemberCardWorkoutSummary {
  const todayYmd = options?.todayYmd ?? localCalendarDateKey(new Date());
  const trainerSessions = sessions.filter((s) => sessionIsFromTrainer(s, trainerId));

  let latestCompleted: SessionLike | null = null;
  let latestTime = -Infinity;
  for (const s of trainerSessions) {
    if (!sessionCountsAsCompletedWorkout(s)) continue;
    const t = sessionCompletionSortTime(s);
    if (t > latestTime) {
      latestTime = t;
      latestCompleted = s;
    }
  }

  const lastWorkoutLine =
    latestCompleted == null
      ? '—'
      : formatLastWorkoutRelative(calendarDayKeyFromScheduledDate(latestCompleted.scheduledDate), todayYmd);

  const upcoming = trainerSessions
    .filter((s) => {
      if (s.status !== 'scheduled' && s.status !== 'in_progress') return false;
      const dk = calendarDayKeyFromScheduledDate(s.scheduledDate);
      return dk >= todayYmd;
    })
    .sort((a, b) => {
      const da = calendarDayKeyFromScheduledDate(a.scheduledDate);
      const db = calendarDayKeyFromScheduledDate(b.scheduledDate);
      const c = da.localeCompare(db);
      if (c !== 0) return c;
      return String(a.scheduledDate).localeCompare(String(b.scheduledDate));
    });

  const next = upcoming[0];
  const nextWorkoutLine =
    next == null ? '—' : formatNextWorkoutRelative(calendarDayKeyFromScheduledDate(next.scheduledDate), todayYmd);

  return { lastWorkoutLine, nextWorkoutLine };
}

export function computeLast7DaysCompletionFromSessions(
  sessions: SessionLike[],
  trainerId: string,
  dateKeys: string[],
  debugContext?: { memberId?: string }
): MemberLast7DaysCompletion {
  const trainerSessions = sessions.filter((s) => sessionIsFromTrainer(s, trainerId));
  const total = trainerSessions.length;
  const completed = trainerSessions.filter((s) => sessionCountsAsCompletedWorkout(s)).length;

  const keySet = new Set(dateKeys);
  const completedByDay = new Set<string>();
  const completedButOutsideWindow: { scheduledDate: string; dotKey: string }[] = [];
  for (const s of trainerSessions) {
    if (!sessionCountsAsCompletedWorkout(s)) continue;
    const key = sessionDotDayKey(s);
    if (keySet.has(key)) {
      completedByDay.add(key);
    } else {
      completedButOutsideWindow.push({ scheduledDate: s.scheduledDate, dotKey: key });
    }
  }

  const daysWithCompletion = dateKeys.map((k) => completedByDay.has(k));

  if (isMemberLast7DebugLogEnabled()) {
    const excluded = sessions.filter((s) => !sessionIsFromTrainer(s, trainerId));
    const sampleExcluded = excluded.slice(0, 5).map((s) => ({
      status: s.status,
      scheduledDate: s.scheduledDate,
      workoutId: s.workout?.id ?? s.workoutId ?? '(none)',
      usersWorkoutplanId: s.usersWorkoutplanId ?? null,
      assignedBy: s.assignedBy ?? '(null)',
    }));
    const sampleIncluded = trainerSessions.slice(0, 5).map((s) => ({
      status: s.status,
      scheduledDate: s.scheduledDate,
      dotKey: sessionDotDayKey(s),
      inWindow: keySet.has(sessionDotDayKey(s)),
      workoutId: s.workout?.id ?? s.workoutId ?? '(none)',
      usersWorkoutplanId: s.usersWorkoutplanId ?? null,
      assignedBy: s.assignedBy ?? '(null)',
    }));
    logMemberLast7('computeLast7DaysCompletionFromSessions', {
      memberId: debugContext?.memberId,
      trainerIdPreview: trainerId ? `${trainerId.slice(0, 8)}…` : '(empty)',
      dateKeys,
      rawSessionCount: sessions.length,
      afterTrainerFilterCount: trainerSessions.length,
      excludedCount: excluded.length,
      completedTrainerSessions: completed,
      completedDotsFilled: completedByDay.size,
      completedButDotOutside7DayWindow: completedButOutsideWindow.slice(0, 5),
      daysWithCompletion,
      sampleExcluded,
      sampleIncluded,
    });
  }

  return { completed, total, daysWithCompletion };
}

export function emptyLast7DaysCompletion(): MemberLast7DaysCompletion {
  return {
    completed: 0,
    total: 0,
    daysWithCompletion: [false, false, false, false, false, false, false],
  };
}
